<template>
  <MModal @close="$emit('close')" modal-class="md:max-w-lg">
    <div v-if="state === 'loading'" class="p-10">
      <MSpinner class="my-8 mx-auto" />
    </div>
    <div v-else-if="state === 'form'" class="p-6">
      <div class="text-20 text-center mb-5">
        <svg role="presentation" focusable="false" tabindex="-1"   class="icon icon-xl text-gray-500" alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 85 85"><path fill="currentColor" fill-rule="evenodd" d="M69.06 66.4H15.94a2.66 2.66 0 0 1 0-5.3h53.12a2.66 2.66 0 0 1 0 5.3ZM42.5 77.04a2.66 2.66 0 1 1 0-5.32 2.66 2.66 0 0 1 0 5.32Zm-21.25-37.2A21.27 21.27 0 0 1 42.5 18.6a21.27 21.27 0 0 1 21.25 21.25v15.94h-42.5V39.84Zm47.81 15.94V39.84a26.6 26.6 0 0 0-23.9-26.42v-8.1a2.66 2.66 0 1 0-5.32 0v8.1a26.6 26.6 0 0 0-23.9 26.42v15.94a7.98 7.98 0 0 0 0 15.94h19.08a7.8 7.8 0 0 0-.49 2.66 7.98 7.98 0 0 0 15.94 0c0-.94-.2-1.83-.5-2.66h19.1a7.98 7.98 0 0 0 0-15.94Z" clip-rule="evenodd"/></svg>
      </div>
      <p class="subtitle2 text-center mb-3">
        {{ title || $t('comparison_alert.form_title') }}
      </p>
      <p class="text-center mb-5">
        {{ description || $t('comparison_alert.form_description') }}
      </p>
      <CorridorInfo
        class="mb-2 justify-center"
        :country-from="corridor.countryFrom.iso"
        :country-to="corridor.countryTo.iso"
        :currency-from="corridor.currencyFrom.iso"
        :currency-to="corridor.currencyTo.iso"
        :amount="corridor.amount || corridor.defaultAmount"
      />
      <p class="text-center text-14 mb-6">
        <template v-if="currentTab === 'broker'">{{ $t('transfer') }}</template>
        <template v-else>{{ $t('transfer_to') }}</template>
        <strong>{{ $t(`tabs.title_${currentTab}`) }}</strong>
      </p>
      <EmailForm
        :submit-label="$t('email_alert.create_alert')"
        :conditions-text="$t('email_alert.by_continuing')"
        @submit="subscribeEmail"
      />
    </div>
    <HighlightBox v-else-if="state === 'error'" class="text-center" type="warning">
      <p class="subtitle2 mb-4">{{ $t('errors.something_went_wrong') }}</p>
      <p class="mb-6">{{ $t('comparison_alert.alert_not_created') }}</p>
      <button class="btn btn-sm btn-amber" @click="$emit('close')">{{ $t('got_it') }}</button>
    </HighlightBox>
    <HighlightBox v-else class="text-center" type="recommendation">
      <p class="subtitle2 mb-2">{{ $t('thanks') }}</p>
      <p class="mb-3">{{ $t('comparison_alert.alert_created') }}</p>
      <CorridorInfo
        class="mb-2 justify-center"
        :country-from="corridor.countryFrom.iso"
        :country-to="corridor.countryTo.iso"
        :currency-from="corridor.currencyFrom.iso"
        :currency-to="corridor.currencyTo.iso"
        :amount="corridor.amount || corridor.defaultAmount"
      />
      <p class="text-center text-14 mb-4">
        <template v-if="currentTab === 'broker'">{{ $t('transfer') }}</template>
        <template v-else>{{ $t('transfer_to') }}</template>
        <strong>{{ $t(`tabs.title_${currentTab}`) }}</strong>
      </p>
      <p class="mb-4">{{ $t('unsubscribe_anytime') }}</p>
      <button class="btn btn-sm btn-amber" @click="$emit('close')">{{ $t('got_it') }}</button>
    </HighlightBox>
  </MModal>
</template>

<script lang="ts" setup>
import { SignUpPromptView } from '@core/analytics/core-events/moneyTransfer'
import { SignUpPromptType } from '@core/analytics/objects/SignUpPrompt'
import { getTabFilters } from './helpers/process-tabs'
import EmailForm from './email-form.vue'
import type { AlertCreationLocation } from '@core/types/alerts'
import type { PaymentType, Corridor } from '@core/types/imt'

defineEmits(['close'])

const props = withDefaults(
  defineProps<{
    comparisonId: string
    currentTab: PaymentType
    signedIn?: boolean
    creationLocation: AlertCreationLocation
    title?: string
    description?: string
  }>(),
  {
    signedIn: false,
  },
)

const corridor = inject('corridor') as Corridor
const { subscriptions } = useRuntimeConfig().public

const { $lang, $track, $cookie } = useNuxtApp()
const logger = useLogger('subscribe-modal')

const state = ref<'loading' | 'form' | 'error' | 'success'>(props.signedIn ? 'loading' : 'form')

onMounted(async () => {
  await Promise.all([
    $track(SignUpPromptView(SignUpPromptType.ComparisonAlert)),
    ...(props.signedIn ? [subscribeEmail()] : []),
  ])
})

async function subscribeEmail(email?: string) {
  if (!subscriptions.apiEndpoint) {
    return onError('The subscriptions apiEndpoint is missing')
  }
  try {
    state.value = 'loading'
    await $fetch(`${subscriptions.apiEndpoint}/subscriptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscriptionCase: 'SMART_SUBSCRIPTION',
        email,
        userId: $cookie.value.getId(),
        language: $lang,
        comparisonId: props.comparisonId,
        countryFrom: corridor.countryFrom.iso,
        countryTo: corridor.countryTo.iso,
        currencyFrom: corridor.currencyFrom.iso,
        currencyTo: corridor.currencyTo.iso,
        amount: corridor.amount || corridor.defaultAmount,
        params: {
          tabType: props.currentTab,
          ...getTabFilters(props.currentTab),
        },
        creationLocation: props.creationLocation,
      }),
      onResponseError({ response }) {
        onError(response.statusText)
      },
    })
    state.value = 'success'
    setSignedIn()
  } catch (e) {
    onError(e)
  }
}

function onError(error: unknown) {
  state.value = 'error'
  logger.error(error)
}
</script>
